import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { EditorButton, EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorContextState } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useConfigureCanvas from '@editor/pages/projects/blueprint-editor/hooks/useConfigureCanvas'
import { resetCreateBlueprint, setMode } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { postBlueprint } from '@editor/services/Blueprint'
import { getCameraProfile } from '@editor/services/Camera'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { autoCalculatePrintScale } from '@editor/services/PrintScale'
import { withPromiseContained } from '@editor/services/Util'
import { GenericError } from '@editor/services/errors/GenericError'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { FaForwardStep } from 'react-icons/fa6'
import { useSelector } from 'react-redux'

import { SelectionStage, reset, resetWorkingState, setSelectionStage } from '../store'
import useGridMesh from './useGridMesh'

const attentionMessages = {
  [SelectionStage.DRAW]: '正面を指定するために、3点を選択してください。',
  [SelectionStage.ROTATE]:
    '面の位置を確認してください。点を移動して調整することもできます。正面を指定ボタンをクリックして次に進んでください。',
  [SelectionStage.FOCUS]:
    'カメラを回転、ズーム、平行移動してさらに調整できます。面に対して横方向に回転することはできません。',
  [SelectionStage.SAVING]: '正面視野を保存中...',
}

export default function useEditor(contextValues: BlueprintEditorContextState): EditorConfig {
  const { arcballControls, orthographicCamera } = contextValues

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Hooks
  const { orientCamera, setupControls } = useConfigureCanvas(contextValues)
  const { clearGridFromCamera } = useGridMesh()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const areaSwitched = useSelector((state: RootState) => state.page.areaSwitched)

  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)
  const pcdBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingBox)

  const editorElements = useSelector((state: RootState) => state.blueprint.drawings.document.editorElements)

  const isDirty = useSelector((state: RootState) => state.bpBlueprintCreator.isDirty)
  const selectionStage = useSelector((state: RootState) => state.bpBlueprintCreator.selectionStage)
  const workingMask = useSelector((state: RootState) => state.maskPCD.workingMask)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.BlueprintManager, [selectedTool])
  const isPreviousToolSelected = useMemo(
    () => prevSelectedTool === BlueprintEditorTools.BlueprintManager,
    [prevSelectedTool],
  )

  // Refs
  const initializedBlueprintId2DPcd = useRef<string | null>(null)

  /**
   * Initialize camera/controls when:
   * - a new blueprint becomes selected (guarded to avoid snapping during save), or
   * - the Blueprint tool becomes active (even if the blueprint didn't change).
   */
  useEffect(() => {
    if (!selectedBlueprint) return

    const isNewBlueprint = initializedBlueprintId2DPcd.current !== selectedBlueprint.blueprint_id
    const activatingTool = isToolSelected && !isPreviousToolSelected

    if (!isNewBlueprint && !activatingTool) return

    initializedBlueprintId2DPcd.current = selectedBlueprint.blueprint_id
  }, [isToolSelected, isPreviousToolSelected, selectedBlueprint, setupControls, orientCamera])

  /**
   * Save camera profile.
   */
  const onClick = useCallback(async () => {
    if (!arcballControls.current || !project || !inspectionArea || !selectedBlueprint) return

    const profile = getCameraProfile(orthographicCamera.current, arcballControls.current)
    if (!profile) return

    await withPromiseContained(async () => {
      const accessToken = await getAccessToken()
      if (!accessToken) return

      dispatch(setSelectionStage(SelectionStage.SAVING))

      const printScale = pcdBoundingBox ? autoCalculatePrintScale(pcdBoundingBox, 'A4') : selectedBlueprint.print_scale
      const result = await postBlueprint(accessToken, project.project_id, inspectionArea.inspection_area_id, {
        name: selectedBlueprint.name,
        camera_profile: profile,
        masking_region: workingMask
          ? {
              center: workingMask.center,
              rotation: workingMask.rotation,
              extent: workingMask.extent,
              use_mask: true,
            }
          : undefined,
        inspection_items: editorElements.inspectionItems,
        polygons: editorElements.polygons,
        cylinders: editorElements.cylinders,
        print_scale: printScale,
      })

      dispatch(resetCreateBlueprint(result))
      dispatch(setAttentionText({ message: '' }))
    }).catch((err) => {
      showErrorModal(err instanceof GenericError ? err.message : COMMON_MESSAGE)
      dispatch(setSelectionStage(SelectionStage.FOCUS))
    })
  }, [
    project,
    inspectionArea,
    selectedBlueprint,
    editorElements,
    workingMask,
    pcdBoundingBox,
    orthographicCamera,
    arcballControls,
    getAccessToken,
    showErrorModal,
    dispatch,
  ])

  /**
   * Set attention text and cursor when tool is active, according to the selection stage.
   */
  useEffect(() => {
    if (isToolSelected) {
      if (attentionMessages[selectionStage]) {
        dispatch(setAttentionText({ message: attentionMessages[selectionStage] }))
      }

      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousToolSelected) {
      dispatch(setCursor(CursorState.DEFAULT))
      dispatch(setAttentionText({ message: '' }))
      if (arcballControls.current) {
        arcballControls.current.enableRotate = true
      }
    }
  }, [isPreviousToolSelected, isToolSelected, selectedTool, selectionStage, arcballControls, dispatch])

  /**
   * Reset working state when tool is deselected.
   */
  useEffect(() => {
    if (!isCreatingBlueprint) {
      clearGridFromCamera(orthographicCamera.current)
      dispatch(resetWorkingState())
      // Allow re-initialization next time this tool is activated.
      initializedBlueprintId2DPcd.current = null
    }
  }, [isCreatingBlueprint, orthographicCamera, clearGridFromCamera, dispatch])

  /**
   * Reset working state when area is switched.
   * Not merging with above effect for simplicity of dependencies and conditional check.
   */
  useEffect(() => {
    clearGridFromCamera(orthographicCamera.current)
    dispatch(reset())
    // Allow re-initialization next time this tool is activated.
    initializedBlueprintId2DPcd.current = null
  }, [orthographicCamera, areaSwitched, clearGridFromCamera, dispatch])

  /**
   * Reset on unmount.
   */
  useEffect(
    () => () => {
      dispatch(reset())
      // Cleanup guard on unmount
      initializedBlueprintId2DPcd.current = null
    },
    [dispatch],
  )

  /**
   * Depending on the selection stage, the submit button will have different behavior.
   */
  const submitButton = useMemo((): EditorButton => {
    const commonProps = {
      isShown: () => isToolSelected,
    }

    if ([SelectionStage.DRAW, SelectionStage.ROTATE].includes(selectionStage)) {
      return {
        ...commonProps,
        key: 'confirm-create-blueprint',
        label: '正面を指定',
        loadingLabel: '',
        icon: <FaForwardStep />,
        onClick: () => {
          dispatch(setSelectionStage(SelectionStage.FOCUS))
          dispatch(setCursor(CursorState.GRAB))
          if (arcballControls.current) {
            arcballControls.current.enableRotate = false
          }
        },
        isLoading: () => false,
        isDisabled: () => selectionStage === SelectionStage.DRAW,
      }
    }

    return {
      ...commonProps,
      key: 'save-create-blueprint',
      label: '保存',
      loadingLabel: '保存中',
      onClick,
      isLoading: () => selectionStage === SelectionStage.SAVING,
      isDisabled: () => [SelectionStage.SAVING].includes(selectionStage),
    }
  }, [selectionStage, isToolSelected, arcballControls, dispatch, onClick])

  return {
    buttons: {
      submit: submitButton,
      reset: {
        onClick: useCallback(() => {
          dispatch(resetWorkingState())
          if (arcballControls.current) {
            arcballControls.current.enableRotate = true
          }

          dispatch(setMode(Mode.Editor3D))
          setupControls(Mode.Editor3D)
          orientCamera(Mode.Editor3D)
        }, [arcballControls, orientCamera, setupControls, dispatch]),
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: () => !isDirty || selectionStage === SelectionStage.SAVING,
      },
      undo: {
        label: selectionStage === SelectionStage.FOCUS ? '正面の編集に戻る' : undefined,
        isShown: useCallback(
          () => isToolSelected && ![SelectionStage.SAVING].includes(selectionStage),
          [isToolSelected, selectionStage],
        ),
        isDisabled: () => !isDirty || selectionStage === SelectionStage.SAVING,
      },
    },
  }
}
